// Carries the password-reset link's address and code across a page
// reload, for the window between arriving from the email and finishing
// the reset.
//
// Why this has to exist at all: the app's service worker reloads the page
// when it takes control (see client/src/serviceWorker.js's
// 'controllerchange' listener). On a *cold* arrival -- a browser that has
// never opened the site, which is exactly what an emailed link produces
// -- the worker installs, claims the client, and reloads while the
// customer is sitting on the reset screen. That reload wipes Redux, and
// by then EnterCode has already stripped the fragment from the URL, so
// the address and code are gone from both places at once and the guard
// bounces them to /register.
//
// Deliberately sessionStorage, not localStorage: this is scoped to the
// one tab and dies when the tab closes, which matches the lifetime of the
// flow. It is cleared explicitly when the reset completes.
//
// On exposure: the code is short-lived (15 minutes), single-use, and
// already sits in this page's Redux store and in the code input's own
// value, so same-origin script can reach it regardless. What
// sessionStorage does not do is put it anywhere the URL would have --
// the address bar, browser history, bookmarks, a Referer header, or a
// server log -- which is the whole reason the fragment is stripped.
const KEY = 'dcc.reset.link';

const safeSession = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch (err) {
    // Storage can throw outright in some privacy modes. The flow still
    // works without it -- it just cannot survive a reload.
    return null;
  }
};

export const rememberResetLink = ({ email, code }) => {
  const store = safeSession();
  if (!store || !email) return;
  try {
    store.setItem(KEY, JSON.stringify({ email, code: code || '', verified: false }));
  } catch (err) {
    /* nothing to do -- see safeSession */
  }
};

// Recorded once the server has accepted the code, so ResetPassword can
// tell "this code was verified in this tab" from "someone typed
// /new_password into the address bar".
export const markResetCodeVerified = () => {
  const store = safeSession();
  if (!store) return;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return;
    store.setItem(KEY, JSON.stringify({ ...JSON.parse(raw), verified: true }));
  } catch (err) {
    /* ignore */
  }
};

export const recallResetLink = () => {
  const store = safeSession();
  const empty = { email: '', code: '', verified: false };
  if (!store) return empty;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    return {
      email: parsed.email || '',
      code: parsed.code || '',
      verified: !!parsed.verified,
    };
  } catch (err) {
    return empty;
  }
};

export const forgetResetLink = () => {
  const store = safeSession();
  if (!store) return;
  try {
    store.removeItem(KEY);
  } catch (err) {
    /* ignore */
  }
};
